import { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { CheckCircle, ArrowRight, Spinner } from "@phosphor-icons/react";
import Logo from "../components/Logo";
import { pollCheckoutStatus } from "../lib/billing";
import { useAuth } from "../context/AuthContext";

const TIER_NAME = {
  solo_monthly: "Solo",
  duo_monthly: "Duo",
  trio_monthly: "Trio",
  small_fleet_monthly: "Small Fleet",
  growth_monthly: "Growth",
};

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [plan, setPlan] = useState(null);
  const { refreshUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) {
      setStatus("no_session");
      return;
    }
    (async () => {
      const result = await pollCheckoutStatus(sessionId);
      setPlan(TIER_NAME[result.lookup_key] || null);
      if (result.payment_status === "paid") {
        setStatus("paid");
        // Refresh user so dashboard reflects new plan
        try { await refreshUser(); } catch {}
        // Fire conversion events (STANDARD_MAP in analytics.js). A card-required
        // trial fires AddPaymentInfo + StartTrial (value = plan price so Meta can
        // optimise for it); an immediate paid checkout fires Subscribe.
        try {
          const { trackConversion } = await import("../lib/analytics");
          const common = {
            content_name: TIER_NAME[result.lookup_key] || "Subscription",
            value: result.amount ? result.amount / 100 : undefined,
            currency: (result.currency || "usd").toUpperCase(),
            transaction_id: result.session_id,
          };
          if (result.trial) {
            trackConversion("card_added", common);
            trackConversion("trial_start", { ...common, predicted_ltv: result.amount ? (result.amount / 100) * 6 : undefined });
          } else {
            trackConversion("subscribe_complete", { ...common, subscription_id: result.subscription_id || result.session_id });
          }
        } catch {}
      } else if (result.payment_status === "pending") {
        setStatus("pending");
      } else {
        setStatus("failed");
      }
    })();
  }, [sessionId, refreshUser]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex flex-col items-center justify-center px-4 noise-bg" data-testid="payment-success-page">
      <div className="mb-10"><Logo /></div>
      <div className="card-surface p-8 sm:p-12 max-w-md w-full text-center">
        {status === "checking" && (
          <>
            <Spinner size={44} className="text-[#FF5A1F] mx-auto animate-spin mb-6" data-testid="payment-status-checking" />
            <h1 className="font-display text-2xl sm:text-3xl font-semibold text-white mb-3">Confirming your subscription…</h1>
            <p className="text-neutral-400 text-sm">Sit tight — this usually takes a couple of seconds.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle size={56} weight="fill" className="text-emerald-400 mx-auto mb-6" data-testid="payment-status-paid" />
            <h1 className="font-display text-3xl sm:text-4xl font-semibold text-white mb-3">You're in! 🚛</h1>
            <p className="text-neutral-300 text-sm sm:text-base leading-relaxed mb-2">
              {plan ? `Your ${plan} subscription is active.` : "Your subscription is active."}
            </p>
            <p className="text-neutral-400 text-xs sm:text-sm mb-8">A receipt has been emailed. You can manage or cancel anytime from Billing.</p>
            <button
              onClick={() => navigate("/app/dashboard")}
              data-testid="payment-success-cta"
              className="btn-primary rounded-full px-6 py-3 text-sm inline-flex items-center gap-2 mx-auto"
            >
              Go to dashboard <ArrowRight size={14} weight="bold" />
            </button>
          </>
        )}
        {status === "pending" && (
          <>
            <Spinner size={44} className="text-[#FF5A1F] mx-auto animate-spin mb-6" />
            <h1 className="font-display text-2xl font-semibold text-white mb-3">Almost there…</h1>
            <p className="text-neutral-400 text-sm mb-6">Stripe is finalizing your payment. This can take a minute for some bank/card combos.</p>
            <Link to="/app/dashboard" className="btn-primary rounded-full px-6 py-3 text-sm inline-flex items-center gap-2">
              Continue to dashboard <ArrowRight size={14} weight="bold" />
            </Link>
          </>
        )}
        {status === "failed" && (
          <>
            <h1 className="font-display text-2xl font-semibold text-white mb-3">Payment didn't complete</h1>
            <p className="text-neutral-400 text-sm mb-6">Something went wrong. Please try again — no charge was made.</p>
            <Link to="/pricing" className="btn-primary rounded-full px-6 py-3 text-sm inline-flex items-center gap-2" data-testid="payment-failed-retry">
              Try again <ArrowRight size={14} weight="bold" />
            </Link>
          </>
        )}
        {status === "no_session" && (
          <>
            <h1 className="font-display text-2xl font-semibold text-white mb-3">Missing session</h1>
            <p className="text-neutral-400 text-sm mb-6">We couldn't find your checkout session. Please try starting over.</p>
            <Link to="/pricing" className="btn-primary rounded-full px-6 py-3 text-sm inline-flex items-center gap-2">
              Back to pricing <ArrowRight size={14} weight="bold" />
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
